import {
	createAsyncThunk,
	createSlice,
	type PayloadAction,
} from "@reduxjs/toolkit";
import axiosInstance from "@/app/utils/axios";

// Interface for individual blog clap state
interface BlogClapState {
	serverClaps: number; // Current server value (like Medium's oriClapNums)
	pendingClaps: number; // Local pending clicks (like Medium's clapNums)
	isSubmitting: boolean; // Loading state
	timeoutId?: NodeJS.Timeout; // For debounce cleanup
	lastSubmissionTime: number; // Track when last submitted
}

// Main clap state
interface ClapState {
	blogClaps: Record<string, BlogClapState>;
}

// Initial state
const initialState: ClapState = {
	blogClaps: {},
};

// Async thunk to submit claps to API (Medium-style batched submission)
export const submitClaps = createAsyncThunk(
	"clap/submitClaps",
	async (
		{ slug, count }: { slug: string; count?: number },
		{ rejectWithValue, getState },
	) => {
		// If no count provided, use pending claps from state
		const state = getState() as { clap: ClapState };
		const actualCount =
			count !== undefined
				? count
				: state.clap.blogClaps[slug]?.pendingClaps || 0;

		if (actualCount <= 0) {
			return rejectWithValue({ slug, message: "No claps to submit" });
		}
		try {
			const response = await axiosInstance.post(
				`${process.env.NEXT_PUBLIC_API_URL}/api/addLikesToBlog/${slug}`,
				{
					likes_to_add: actualCount,
				},
			);

			return {
				slug,
				...response.data,
			};
		} catch (error: any) {
			return rejectWithValue({
				slug,
				message: error.response?.data?.message || "Failed to submit claps",
			});
		}
	},
);

const clapSlice = createSlice({
	name: "clap",
	initialState,
	reducers: {
		// Initialize blog clap state if it doesn't exist
		initializeBlogClaps: (
			state,
			action: PayloadAction<{ slug: string; initialLikes: number }>,
		) => {
			const { slug, initialLikes } = action.payload;
			if (!state.blogClaps[slug]) {
				state.blogClaps[slug] = {
					serverClaps: initialLikes || 0,
					pendingClaps: 0,
					isSubmitting: false,
					lastSubmissionTime: 0,
				};
			}
		},

		// Add a clap (Medium-style immediate UI update)
		addClap: (state, action: PayloadAction<string>) => {
			const slug = action.payload;
			if (!state.blogClaps[slug]) {
				state.blogClaps[slug] = {
					serverClaps: 0,
					pendingClaps: 0,
					isSubmitting: false,
					lastSubmissionTime: 0,
				};
			}

			const blogState = state.blogClaps[slug];
			const totalClaps = blogState.serverClaps + blogState.pendingClaps;

			// Only add clap if under 50 limit
			if (totalClaps < 50) {
				blogState.pendingClaps += 1;
			}
		},

		// Store timeout ID for debounce cleanup
		setTimeoutId: (
			state,
			action: PayloadAction<{ slug: string; timeoutId: NodeJS.Timeout }>,
		) => {
			const { slug, timeoutId } = action.payload;
			if (state.blogClaps[slug]) {
				// Clear existing timeout if it exists
				if (state.blogClaps[slug].timeoutId) {
					clearTimeout(state.blogClaps[slug].timeoutId);
				}
				state.blogClaps[slug].timeoutId = timeoutId;
			}
		},

		// Clear timeout (for cleanup)
		clearTimeout: (state, action: PayloadAction<string>) => {
			const slug = action.payload;
			if (state.blogClaps[slug]?.timeoutId) {
				clearTimeout(state.blogClaps[slug].timeoutId);
				state.blogClaps[slug].timeoutId = undefined;
			}
		},

		// Clear pending claps (after successful submission)
		clearPendingClaps: (state, action: PayloadAction<string>) => {
			const slug = action.payload;
			if (state.blogClaps[slug]) {
				state.blogClaps[slug].pendingClaps = 0;
			}
		},

		// Reset blog clap state
		resetBlogClaps: (state, action: PayloadAction<string>) => {
			const slug = action.payload;
			if (state.blogClaps[slug]) {
				// Clear timeout if exists
				if (state.blogClaps[slug].timeoutId) {
					clearTimeout(state.blogClaps[slug].timeoutId);
				}
				delete state.blogClaps[slug];
			}
		},
	},
	extraReducers: (builder) => {
		builder
			// Handle pending submission
			.addCase(submitClaps.pending, (state, action) => {
				const slug = action.meta.arg.slug;
				if (state.blogClaps[slug]) {
					state.blogClaps[slug].isSubmitting = true;
				}
			})

			// Handle successful submission (Medium-style server update)
			.addCase(submitClaps.fulfilled, (state, action) => {
				const { slug, current_likes, likes_added } = action.payload;
				if (state.blogClaps[slug]) {
					state.blogClaps[slug].serverClaps = current_likes;
					state.blogClaps[slug].pendingClaps = 0; // Clear pending after success
					state.blogClaps[slug].isSubmitting = false;
					state.blogClaps[slug].lastSubmissionTime = Date.now();

					// Clear timeout
					if (state.blogClaps[slug].timeoutId) {
						clearTimeout(state.blogClaps[slug].timeoutId);
						state.blogClaps[slug].timeoutId = undefined;
					}
				}
			})

			// Handle submission failure
			.addCase(submitClaps.rejected, (state, action) => {
				const slug = action.meta.arg.slug;
				if (state.blogClaps[slug]) {
					state.blogClaps[slug].isSubmitting = false;

					// Clear timeout
					if (state.blogClaps[slug].timeoutId) {
						clearTimeout(state.blogClaps[slug].timeoutId);
						state.blogClaps[slug].timeoutId = undefined;
					}

					// On error, keep pending claps for retry
					// Could implement retry logic here
				}
			});
	},
});

// Export actions
export const {
	initializeBlogClaps,
	addClap,
	setTimeoutId,
	clearTimeout: clearClapTimeout,
	clearPendingClaps,
	resetBlogClaps,
} = clapSlice.actions;

// Selectors (similar to Medium's state access pattern)
export const selectBlogClaps = (
	state: { clap: ClapState },
	slug: string,
): BlogClapState | undefined => {
	return state.clap.blogClaps[slug];
};

export const selectTotalClaps = (
	state: { clap: ClapState },
	slug: string,
): number => {
	const blogState = state.clap.blogClaps[slug];
	if (!blogState) return 0;
	return blogState.serverClaps + blogState.pendingClaps;
};

export const selectCanClap = (
	state: { clap: ClapState },
	slug: string,
): boolean => {
	const totalClaps = selectTotalClaps(state, slug);
	return totalClaps < 50;
};

export const selectIsSubmitting = (
	state: { clap: ClapState },
	slug: string,
): boolean => {
	return state.clap.blogClaps[slug]?.isSubmitting || false;
};

export default clapSlice.reducer;
